import React from "react"

import {
  customerMembershipActivityList,
  getCustomerMembershipActivityListQueryKey,
} from "@boundless/bl-api-client/customer/services"
import { useSuspenseInfiniteQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"

import { CompanyFilter } from "@/components/membership/CompanyFilter"
import {
  FallbackMembershipActivityTable,
  MembershipActivityTable,
} from "@/components/membership/MembershipActivityTable"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

export const Route = createFileRoute("/_dashboard/_customer/users/activity")({
  component: ActivityView,
})

function ActivityList({ companyUuid }: { companyUuid: string }) {
  // Page by page rather than a pager: the borrower app has no pager component,
  // and one list with a Load more button does not justify building the first.
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuspenseInfiniteQuery({
      queryKey: [
        ...getCustomerMembershipActivityListQueryKey(companyUuid),
        "infinite",
      ],
      queryFn: ({ pageParam }) =>
        customerMembershipActivityList(companyUuid, { page: pageParam }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.next ? allPages.length + 1 : undefined,
    })

  // Offset paging over a list that grows at the head, so a change landing
  // between two fetches can repeat a row across pages. Deduplicated here
  // rather than by switching the endpoint to cursor pagination.
  const rows = [
    ...new Map(
      data.pages
        .flatMap((page) => page.results)
        .map((activity) => [activity.uuid, activity]),
    ).values(),
  ]

  return (
    <div className="space-y-4">
      <MembershipActivityTable activities={rows} />
      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}
    </div>
  )
}

function ActivityView() {
  const { companyUuid } = Route.useRouteContext()
  const navigate = Route.useNavigate()

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Activity</CardTitle>
        <Button asChild variant="link">
          <Link to="/users" search={{ company: companyUuid }}>
            Users
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <CompanyFilter
          companyUuid={companyUuid}
          onSelect={(uuid) => navigate({ search: { company: uuid } })}
        />
        {/* Keyed so switching company shows the skeleton again instead of the
            previous company's rows while the next list loads. */}
        <React.Suspense
          key={companyUuid}
          fallback={<FallbackMembershipActivityTable />}
        >
          <ActivityList companyUuid={companyUuid} />
        </React.Suspense>
      </CardContent>
    </Card>
  )
}
